import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Loader } from '@common/components';
import { useSyncDayDetailQuery, useLazySyncS3ObjectQuery } from '@dashboard/api/syncMonitor';
import DeliveryModeBadge from '@dashboard/components/SyncMonitor/DeliveryModeBadge';
import TagBadge from '@dashboard/components/SyncMonitor/TagBadge';
import CopyId from '@dashboard/components/SyncMonitor/CopyId';
import classes from './DayDetailBody.module.scss';

const TERMINAL_SUCCESS = new Set(['PROCESSED', 'DUPLICATE', 'REINGESTED']);

const NOTE_KEYS = {
  legacy_original: 'noteLegacyOriginal',
  duplicate_skipped: 'noteDuplicateSkipped',
  regenerated_over_legacy: 'noteRegeneratedOverLegacy',
};

export function StatusChip({ status }) {
  return (
    <span className={`${classes.chip} ${classes[`chip_${status}`]}`}>
      {status}
    </span>
  );
}
StatusChip.propTypes = { status: PropTypes.string };

function formatDate(value) {
  if (!value) return '—';
  return value.replace('T', ' ').slice(0, 19);
}

function groupFilesByPair(files) {
  const pairs = new Map();
  files.forEach((f) => {
    const key = f.pair_id ?? `__no_pair_${f.id}`;
    if (!pairs.has(key)) pairs.set(key, []);
    pairs.get(key).push(f);
  });

  const entries = Array.from(pairs.entries()).map(([pairId, rows]) => {
    const sig = rows.map((r) => r.filename || '').sort().join('|');
    const isSuccess = rows.some((r) => TERMINAL_SUCCESS.has(r.status));
    const minId = rows.map((r) => r.id).sort()[0];
    return { pairId, rows, sig, isSuccess, minId };
  });

  const sigCounts = new Map();
  entries.forEach((e) => sigCounts.set(e.sig, (sigCounts.get(e.sig) || 0) + 1));

  const sigPrimary = new Map();
  entries.forEach((e) => {
    if (sigCounts.get(e.sig) <= 1) return;
    const current = sigPrimary.get(e.sig);
    if (!current
      || (e.isSuccess && !current.isSuccess)
      || (e.isSuccess === current.isSuccess && e.minId < current.minId)) {
      sigPrimary.set(e.sig, e);
    }
  });

  const annotated = entries.map((e) => ({
    ...e,
    isDuplicateGroup: sigCounts.get(e.sig) > 1,
    isResend: sigCounts.get(e.sig) > 1 && sigPrimary.get(e.sig)?.pairId !== e.pairId,
  }));

  annotated.sort((a, b) => {
    if (a.isResend !== b.isResend) return a.isResend ? 1 : -1;
    return a.minId < b.minId ? -1 : 1;
  });

  return annotated;
}

function FilesTable({ files, t }) {
  if (!files || !files.length) return null;
  const groups = groupFilesByPair(files);
  const hasMultiple = groups.length > 1;

  return (
    <div className={classes.section}>
      <h4 className={classes.sectionTitle}>
        {t('drawerFiles')}
        {hasMultiple && (
          <span className={classes.sectionCount}>
            ({groups.length} {t('pairCountSuffix')})
          </span>
        )}
      </h4>
      <table className={classes.table}>
        <thead>
          <tr>
            <th>Pair</th><th>Type</th><th>Status</th><th>Exported</th><th>Received</th><th>Filename</th><th>Error</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g, idx) => g.rows.map((f, rowIdx) => (
            <tr key={f.id} className={g.isResend ? classes.pairDup : ''}>
              <td>
                {rowIdx === 0 ? (
                  <span className={`${classes.pairBadge} ${g.isResend ? classes.pairBadgeDup : classes.pairBadgePrimary}`}>
                    {`#${idx + 1}`}{g.isResend ? ` · ${t('pairResend')}` : ''}
                  </span>
                ) : null}
              </td>
              <td>{f.data_type}</td>
              <td><StatusChip status={f.status?.toLowerCase()} /></td>
              <td className={classes.dim}>{formatDate(f.exported_at)}</td>
              <td className={classes.dim}>{formatDate(f.created_at)}</td>
              <td className={classes.mono}>{f.filename}</td>
              <td className={classes.error}>{f.error_txt || '—'}</td>
            </tr>
          )))}
        </tbody>
      </table>
    </div>
  );
}
FilesTable.propTypes = { files: PropTypes.array, t: PropTypes.func };

function S3Table({ uploads, t }) {
  if (!uploads || !uploads.length) return null;
  return (
    <div className={classes.section}>
      <h4 className={classes.sectionTitle}>
        {t('drawerS3')}
        <span className={classes.sectionCount}>({uploads.length})</span>
      </h4>
      <table className={classes.table}>
        <thead>
          <tr>
            <th>Type</th><th>Status</th><th>Queued</th><th>Uploaded</th><th>Error</th>
          </tr>
        </thead>
        <tbody>
          {uploads.map((s) => (
            <tr key={s.id}>
              <td>{s.type}</td>
              <td><StatusChip status={s.status?.toLowerCase()} /></td>
              <td className={classes.dim}>{formatDate(s.queued_at)}</td>
              <td className={classes.dim}>{formatDate(s.uploaded_at)}</td>
              <td className={classes.error}>{s.error || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
S3Table.propTypes = { uploads: PropTypes.array, t: PropTypes.func };

function LiveCheck({ centerId, date, type, t }) {
  const [check, { data, isFetching }] = useLazySyncS3ObjectQuery();
  const live = data?.data;

  let result = null;
  if (live) {
    if (!live.available) {
      result = <span className={classes.error}>{t('remoteLiveFailed')}</span>;
    } else if (!live.exists) {
      result = <span className={classes.error}>{t('remoteLiveMissing')}</span>;
    } else {
      result = (
        <span className={classes.mono} title={live.version_id || ''}>
          {live.version_id ? `v ${live.version_id.slice(0, 10)}…` : t('remoteLiveNoVersion')}
        </span>
      );
    }
  }

  return (
    <>
      <button
        type="button"
        className={classes.liveBtn}
        disabled={isFetching}
        onClick={() => check({ centerId, date, type })}
      >
        {isFetching ? '…' : t('remoteCheckLive')}
      </button>{' '}
      {result}
    </>
  );
}
LiveCheck.propTypes = {
  centerId: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  t: PropTypes.func,
};

function RemoteTable({ remote, centerId, date, t }) {
  if (!remote) return null;
  const { status } = remote;
  const objects = remote.objects || {};
  const mismatch = remote.mismatch || [];

  if (!status?.available) {
    // Disabled, never scanned or stale: say why instead of showing guesses.
    if (!status?.enabled) return null;
    return (
      <div className={classes.section}>
        <h4 className={classes.sectionTitle}>{t('drawerRemote')}</h4>
        <div className={classes.notes}>
          <span className={`${classes.note} ${classes.noteWarn}`}>
            {t(`remoteUnavailable_${status?.reason || 'unknown'}`, t('remoteUnavailable_unknown'))}
          </span>
        </div>
      </div>
    );
  }

  const asOf = status.last_success_at ? formatDate(status.last_success_at) : '—';

  return (
    <div className={classes.section}>
      <h4 className={classes.sectionTitle}>
        {t('drawerRemote')}
        <span className={classes.sectionCount}>({t('remoteAsOf', { date: asOf })})</span>
      </h4>
      {mismatch.length > 0 && (
        <div className={classes.notes}>
          {mismatch.map((m) => (
            <span key={`${m.type}-${m.code}`} className={`${classes.note} ${classes.noteWarn}`}>
              {`${m.type}: ${t(`remoteMismatch_${m.code}`)}`}
            </span>
          ))}
        </div>
      )}
      <table className={classes.table}>
        <thead>
          <tr>
            <th>Type</th><th>In S3</th><th>Size</th><th>Modified (UTC)</th><th>Overwrites</th><th>Live</th>
          </tr>
        </thead>
        <tbody>
          {['BENCHMARK', 'USAGE'].map((type) => {
            const o = objects[type];
            return (
              <tr key={type}>
                <td>{type}</td>
                <td>{o?.exists ? '✓' : '—'}</td>
                <td className={classes.dim}>{o?.exists ? `${o.size} B` : '—'}</td>
                <td className={classes.dim}>{o?.exists ? formatDate(o.last_modified) : '—'}</td>
                <td className={classes.dim}>{o?.exists ? o.change_count : '—'}</td>
                <td>
                  <LiveCheck centerId={centerId} date={date} type={type} t={t} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
RemoteTable.propTypes = {
  remote: PropTypes.object,
  centerId: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  t: PropTypes.func,
};

function JobsTable({ jobs, t }) {
  if (!jobs || !jobs.length) return null;
  return (
    <div className={classes.section}>
      <h4 className={classes.sectionTitle}>{t('drawerJob')}</h4>
      <table className={classes.table}>
        <thead>
          <tr>
            <th>Type</th><th>Status</th><th>Attempts</th><th>Next retry</th><th>HTTP</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((j) => (
            <tr key={j.id}>
              <td>{j.data_type}</td>
              <td><StatusChip status={j.status?.toLowerCase()} /></td>
              <td>{j.attempt_count}</td>
              <td>{j.next_retry_at || '—'}</td>
              <td>{j.http_status || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
JobsTable.propTypes = { jobs: PropTypes.array, t: PropTypes.func };

export default function DayDetailBody({ centerId, date, showMeta }) {
  const { t } = useTranslation('dashboard\\SyncMonitorView', {
    useSuspense: false,
  });

  const { data, isFetching, isError } = useSyncDayDetailQuery(
    { centerId, date },
    { skip: !centerId || !date },
  );

  if (isFetching) {
    return (
      <div className={classes.loaderWrap}>
        <Loader />
      </div>
    );
  }

  if (isError) {
    return <p className={classes.errorMsg}>{t('detailLoadError')}</p>;
  }

  const detail = data?.data;
  if (!detail) return null;

  return (
    <div className={classes.body}>
      {showMeta && (
        <div className={classes.meta}>
          <p>
            <strong>{detail.center_name}</strong> &mdash; {detail.date}
          </p>
          {detail.external_id ? (
            <p>
              <CopyId value={detail.external_id} />
            </p>
          ) : null}
          <div className={classes.metaChips}>
            <StatusChip status={detail.status} />
            {detail.delivery_mode ? (
              <DeliveryModeBadge mode={detail.delivery_mode} size="sm" />
            ) : null}
            {(detail.tags || []).map((tag) => (
              <TagBadge key={tag} tag={tag} />
            ))}
          </div>
        </div>
      )}

      {detail.notes && detail.notes.length > 0 && (
        <div className={classes.notes}>
          {detail.notes.map((n) => (
            <span
              key={n}
              className={`${classes.note} ${n === 'regenerated_over_legacy' ? classes.noteWarn : ''}`}
            >
              {t(NOTE_KEYS[n] ?? n)}
            </span>
          ))}
        </div>
      )}

      <FilesTable files={detail.files} t={t} />
      <S3Table uploads={detail.s3_uploads} t={t} />
      <RemoteTable remote={detail.remote} centerId={centerId} date={date} t={t} />
      <JobsTable jobs={detail.jobs} t={t} />
    </div>
  );
}

DayDetailBody.propTypes = {
  centerId: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  showMeta: PropTypes.bool,
};

DayDetailBody.defaultProps = {
  showMeta: false,
};
